import React, { useEffect, useState } from "react";
import containerImg from "../assets/container.jpg";
import backpackImg from "../assets/backpack.jpg";
import {
  fetchContainerWithCheck,
  strapiRemoteUri,
  deleteItem,
} from "../strapi/queries";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete"; // Re-add DeleteIcon for the top bar
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import {
  Grid,
  Card,
  Chip,
  CardActions,
  CardContent,
  Typography,
  Button,
  CardMedia,
  Box,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AddIcon from "@mui/icons-material/Add";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { useNavigate, useParams } from "react-router-dom";
import AddContainerModal from "./AddContainerModal";
import AddItemModal from "./AddItemModal";
import CheckModal from "./CheckModal";
import LatestCheckModal from "./LatestCheckModal";
import { getGradientColor } from "../gradientColor";

/**
 * Calculates the remaining lifetime progress as a percentage
 * @param {string} purchaseDate - The date of purchase in ISO format
 * @param {number} lifetimeInYears - The lifespan in years
 * @returns {number} - The progress percentage (0-100)
 */
const calculateProgress = (purchaseDate, lifetimeInYears) => {
  const purchaseDateObj = dayjs(purchaseDate);
  const expirationDate = purchaseDateObj.add(lifetimeInYears, "year");
  const currentDate = dayjs();
  const totalLifetimeInDays = expirationDate.diff(purchaseDateObj, "day");
  const elapsedTimeInDays = currentDate.diff(purchaseDateObj, "day");
  const remainingTimeInDays = totalLifetimeInDays - elapsedTimeInDays;
  const progress = parseInt((remainingTimeInDays / totalLifetimeInDays) * 100);
  return progress;
};

/**
 * Truncates a string to a maximum length and adds ellipsis if needed
 * @param {string} str - The string to truncate
 * @param {number} maxLength - The maximum length
 * @returns {string} - The truncated string
 */
function truncateString(str, maxLength) {
  if (typeof str !== "string") return "";
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + "...";
}

/**
 * Gets the URL of a thumbnail image for an item based on color
 * @param {Object} item - The item object
 * @param {string} color - The color to match
 * @returns {string|null} - The URL of the thumbnail image or null if not found
 */
function getPicUrlByColor(item, color) {
  if (!item || !item.variant || !Array.isArray(item.variant)) {
    return null;
  }

  // Find variant with matching color
  const matchedVariant = item.variant.find(
    (variant) => variant.color === color
  );

  if (
    matchedVariant &&
    matchedVariant.pic &&
    matchedVariant.pic.formats &&
    matchedVariant.pic.formats.thumbnail
  ) {
    return matchedVariant.pic.formats.thumbnail.url;
  }

  // Fallback to first variant with an image
  const fallbackVariant = item.variant.find(
    (variant) => variant.pic?.formats?.thumbnail?.url
  );

  return fallbackVariant ? fallbackVariant.pic.formats.thumbnail.url : null;
}

/**
 * Renders a progress bar for the remaining lifetime
 * @param {string} produceDate - The produce date of in ISO format
 * @param {number} lifespan - The lifespan in years
 * @returns {JSX.Element} - The progress bar component
 */
const ItemProgressBar = ({
  type,
  produceDate,
  lifespan,
  latestCheck,
  onClick,
}) => {
  if (type === "lifetime") {
    if (!lifespan) {
      return (
        <Chip
          icon={<InfoOutlineIcon sx={{ height: "18px" }} color="black" />}
          label="Kein Ablaufdatum"
          size="small"
          sx={{
            borderRadius: "5px",
            marginTop: "10px",
            width: "100%",
            height: "20px",
            backgroundColor: "#4ecc42ff",
          }}
        />
      );
    }
    if (!produceDate) {
      return (
        <Chip
          icon={<InfoOutlineIcon sx={{ height: "18px" }} color="black" />}
          label="Unbekannt"
          size="small"
          sx={{
            borderRadius: "5px",
            marginTop: "10px",
            width: "100%",
            height: "20px",
            backgroundColor: "#ff9100ff",
          }}
        />
      );
    }

    let progress = calculateProgress(produceDate, lifespan);

    if (progress <= 0) {
      return (
        <Chip
          icon={<WarningAmberIcon sx={{ height: "18px" }} color="black" />}
          label="Abgelaufen"
          size="small"
          sx={{
            borderRadius: "5px",
            marginTop: "10px",
            width: "100%",
            height: "20px",
            backgroundColor: "#fc7676",
          }}
        />
      );
    }

    const text = progress + " %";
    const wid = Math.max(progress, "25") + "%";
    return (
      <Chip
        label={text}
        size="small"
        sx={{
          borderRadius: "5px",
          marginTop: "10px",
          width: wid,
          height: "20px",
          backgroundColor: getGradientColor(progress),
        }}
      />
    );
  }
  if (type === "check") {
    if (!latestCheck) {
      return (
        <Chip
          icon={<WarningAmberIcon sx={{ height: "18px" }} color="black" />}
          label="Nie geprüft"
          size="small"
          sx={{
            borderRadius: "5px",
            marginTop: "10px",
            width: "100%",
            height: "20px",
            backgroundColor: "#ff9100ff",
          }}
        />
      );
    }

    const today = dayjs();
    const nextDate = dayjs(latestCheck.next_date);
    const diffDays = nextDate.diff(today, "day");
    let progress = Math.min(diffDays, 100);

    if (latestCheck.result === "fail") {
      return (
        <Chip
          icon={<WarningAmberIcon sx={{ height: "18px" }} color="black" />}
          label="Prüfung nicht bestanden"
          onClick={onClick}
          size="small"
          sx={{
            borderRadius: "5px",
            marginTop: "10px",
            width: "100%",
            height: "20px",
            backgroundColor: "#fc7676",
          }}
        />
      );
    }
    if (progress <= 0) {
      return (
        <Chip
          icon={<WarningAmberIcon sx={{ height: "18px" }} color="black" />}
          label="Prüfung abgelaufen"
          onClick={onClick}
          size="small"
          sx={{
            borderRadius: "5px",
            marginTop: "10px",
            width: "100%",
            height: "20px",
            backgroundColor: "#fc7676",
          }}
        />
      );
    }

    const text = progress + " %";
    const wid = Math.max(progress, "25") + "%";
    return (
      <Chip
        label={text}
        size="small"
        onClick={onClick}
        sx={{
          borderRadius: "5px",
          marginTop: "10px",
          width: wid,
          height: "20px",
          backgroundColor: getGradientColor(progress),
        }}
      />
    );
  }
};

/**
 * Renders a summary progress bar for grouped items, indicating unchecked, expired, or failed check statuses.
 * @param {boolean} hasNeverCheckedItems - True if there are items that have never been checked.
 * @param {boolean} hasExpiredItems - True if there are items with expired lifetime.
 * @param {boolean} hasFailedCheckItems - True if there are items with failed checks.
 * @returns {JSX.Element|null} - The progress bar component or null if no issues.
 */
const GroupItemProgressBar = ({
  hasNeverCheckedItems,
  hasExpiredItems,
  hasFailedCheckItems,
}) => {
  const chips = [];

  if (hasNeverCheckedItems) {
    chips.push(
      <Chip
        key="neverChecked"
        icon={<WarningAmberIcon sx={{ height: "18px" }} color="black" />}
        label="Hat ungeprüfte Produkte"
        size="small"
        sx={{
          borderRadius: "5px",
          marginTop: chips.length > 0 ? "10px" : "0px",
          width: "100%",
          height: "20px",
          backgroundColor: "#ff9100ff",
        }}
      />
    );
  }

  if (hasExpiredItems) {
    chips.push(
      <Chip
        key="expired"
        icon={<WarningAmberIcon sx={{ height: "18px" }} color="black" />}
        label="Hat abgelaufene Produkte"
        size="small"
        sx={{
          borderRadius: "5px",
          marginTop: chips.length > 0 ? "10px" : "0px",
          width: "100%",
          height: "20px",
          backgroundColor: "#fc7676",
        }}
      />
    );
  }

  if (hasFailedCheckItems) {
    chips.push(
      <Chip
        key="failedCheck"
        icon={<WarningAmberIcon sx={{ height: "18px" }} color="black" />}
        label="Hat unbestandene Prüfungen"
        size="small"
        sx={{
          borderRadius: "5px",
          marginTop: chips.length > 0 ? "10px" : "0px",
          width: "100%",
          height: "20px",
          backgroundColor: "#fc7676",
        }}
      />
    );
  }

  if (chips.length === 0) {
    chips.push(
      <Chip
        key="allGood"
        icon={<CheckIcon sx={{ height: "18px" }} color="black" />}
        label="Alles geprüft und gültig"
        size="small"
        sx={{
          borderRadius: "5px",
          marginTop: chips.length > 0 ? "10px" : "0px",
          width: "100%",
          height: "20px",
          backgroundColor: "#4ecc42ff",
        }}
      />
    );
  }

  return <>{chips}</>;
};

/**
 * Card component for a container
 * @param {Object} container - The container object
 * @param {Function} onOpen - Function to call when the Open button is clicked
 * @param {Function} onSelect - Function to call when the checkbox is clicked
 * @param {boolean} isSelected - Whether the container is selected
 * @returns {JSX.Element} - The container card component
 */
const ContainerCard = ({
  container,
  onOpen,
  onSelect,
  isSelected,
  canSelect,
}) => {
  const isBackpack =
    container.name.includes("Rucksack") || container.name.includes("rucksack");
  let icon = isBackpack ? backpackImg : containerImg;
  if (container?.image?.formats?.thumbnail) {
    icon = strapiRemoteUri + container.image.formats.thumbnail.url;
  }
  return (
    <Card
      sx={{
        width: "250px",
        height: "420px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Typography variant="subtitle2" color="text.secondary">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <LocalOfferIcon style={{ marginRight: "5px" }} />
                <span>{"Behälter"}</span>
              </div>
              {canSelect && (
                <Tooltip title={isSelected ? "Abwählen" : "Auswählen"}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect({
                        type: "container",
                        id: container.documentId,
                        name: container.name,
                      });
                    }}
                    sx={{
                      color: "#10527e",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    {isSelected ? (
                      <CheckBoxIcon />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              component="div"
              sx={{ flexGrow: 1 }}
            >
              {truncateString(container.name, 20)}
            </Typography>
          </Box>
          <CardMedia
            component="img"
            sx={{
              width: "auto",
              maxHeight: 185,
              maxWidth: 170,
              mt: "auto",
              mb: "auto",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
            image={icon}
            alt={container.name}
          />
          <Box sx={{ mt: 1 }}>
            {container.counts?.container_cnt > 0 && (
              <Typography variant="body2">
                Anzahl Behälter: {container.counts.container_cnt}
              </Typography>
            )}
            {container.counts?.item_cnt > 0 && (
              <Typography variant="body2">
                Anzahl Items: {container.counts.item_cnt}
              </Typography>
            )}
            <hr style={{ borderTop: "1px solid #eee", margin: "8px 0" }} />
            <GroupItemProgressBar
              hasNeverCheckedItems={!!container.counts.unchecked}
              hasExpiredItems={!!container.counts.due}
              hasFailedCheckItems={!!container.counts.failed}
            />
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ mt: "auto", justifyContent: "center" }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onOpen(container.documentId)}
          sx={{
            color: "#10527e",
            borderColor: "#10527e",
            marginBottom: "10px",
          }}
        >
          Öffnen
        </Button>
      </CardActions>
    </Card>
  );
};

/**
 * Card component for a single item
 * @param {Object} item - The item object
 * @returns {JSX.Element} - The item card component
 */
const SingleItemCard = ({
  item,
  onSelect,
  isSelected,
  setShowLatestCheckModal,
  setLatestCheckData,
  canSelect,
}) => {
  const picUrl = getPicUrlByColor(item.item, item.color);

  return (
    <Card
      sx={{
        width: "250px",
        height: "420px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Typography variant="subtitle2" color="text.secondary">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <LocalOfferIcon style={{ marginRight: "5px" }} />
                <span>{truncateString(item.item?.category?.name, 20)}</span>
              </div>
              {canSelect && (
                <Tooltip title={isSelected ? "Abwählen" : "Auswählen"}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect({
                        type: "containerItem",
                        id: item.documentId,
                        name: item.item?.name,
                        category: item.item?.category?.name,
                      });
                    }}
                    sx={{
                      color: "#10527e",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    {isSelected ? (
                      <CheckBoxIcon />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              component="div"
              sx={{ flexGrow: 1 }}
            >
              {truncateString(item.item?.name, 20)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Hersteller: {item.item?.producer?.name}
          </Typography>
          {picUrl && (
            <CardMedia
              component="img"
              sx={{
                width: "auto",
                maxHeight: 100,
                maxWidth: 100,
                mt: "auto",
                mb: "auto",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
              image={strapiRemoteUri + picUrl}
              alt={item.item?.name || ""}
            />
          )}
          <Box sx={{ mt: 0 }}>
            <Typography component={"span"} variant="body2">
              {item.serial && (
                <>
                  Seriennr: {item.serial} <br />
                  <hr
                    style={{ borderTop: "1px solid #eee", margin: "8px 0" }}
                  />
                </>
              )}
              {item.produce_date && (
                <>
                  Herstelldatum: {dayjs(item.produce_date).format("MM/YYYY")}{" "}
                  <br />
                </>
              )}
              {!item.produce_date && item.purchase_date && (
                <>
                  Kaufdatum: {dayjs(item.purchase_date).format("DD.MM.YYYY")}{" "}
                  <br />
                </>
              )}
              {item.lifespan > 0 && <>Lebensdauer: {item.lifespan} Jahre</>}
              <div style={{ width: "100%" }}>
                <ItemProgressBar
                  type={"lifetime"}
                  produceDate={item.produce_date || item.purchase_date}
                  lifespan={item.lifespan}
                />
              </div>
              <hr style={{ borderTop: "1px solid #eee", margin: "8px 0" }} />
              {item.checks?.length > 0 ? (
                (() => {
                  const latestCheck = [...item.checks].sort(
                    (a, b) => new Date(b.date) - new Date(a.date)
                  )[0];

                  return (
                    <>
                      Letzte Prüfung:{" "}
                      {dayjs(latestCheck.date).format("DD.MM.YYYY")}
                      <br />
                      Nächste Prüfung:{" "}
                      {dayjs(latestCheck.next_date).format("DD.MM.YYYY")}
                      <br />
                      <div style={{ width: "100%" }}>
                        <ItemProgressBar
                          type={"check"}
                          latestCheck={latestCheck}
                          onClick={() => {
                            setShowLatestCheckModal(true);
                            setLatestCheckData(latestCheck);
                          }}
                        />
                      </div>
                    </>
                  );
                })()
              ) : (
                <>
                  Letzte Prüfung: Keine
                  <br />
                  Nächste Prüfung: Keine
                  <br />
                  <div style={{ width: "100%" }}>
                    <ItemProgressBar type={"check"} latestCheck={null} />
                  </div>
                </>
              )}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * Card component for a group of items with the same documentId
 * @param {Object} group - The group object containing items array and metadata
 * @param {string} containerId - The current container ID
 * @param {Function} onOpen - Function to call when the Open button is clicked
 * @param {Function} onSelect - Function to call when the checkbox is clicked
 * @param {boolean} isSelected - Whether the group is selected
 * @returns {JSX.Element} - The group card component
 */
const GroupedItemCard = ({
  group,
  containerId,
  onOpen,
  onSelect,
  isSelected,
  canSelect,
}) => {
  const firstItem = group.items[0];
  const picUrl = getPicUrlByColor(firstItem.item, firstItem.color);

  const hasExpiredItems = group.items.some((item) => {
    const progress = calculateProgress(
      item.produce_date || item.purchase_date,
      item.lifespan
    );
    return item.lifespan > 0 && progress <= 0;
  });

  const hasNeverCheckedItems = group.items.some((item) => {
    const latestCheck =
      item.checks?.length > 0
        ? [...item.checks].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        )[0]
        : null;
    return !latestCheck;
  });

  const hasFailedCheckItems = group.items.some((item) => {
    const latestCheck =
      item.checks?.length > 0
        ? [...item.checks].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        )[0]
        : null;
    return latestCheck && latestCheck.result === "fail";
  });

  return (
    <Card
      sx={{
        width: "250px",
        height: "420px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Typography variant="subtitle2" color="text.secondary">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <LocalOfferIcon style={{ marginRight: "5px" }} />
                <span>{truncateString(firstItem.item.category.name, 20)}</span>
              </div>
              {canSelect && (
                <Tooltip title={isSelected ? "Abwählen" : "Auswählen"}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect({
                        type: "containerItem",
                        ids: group.items.map((item) => item.documentId),
                        name: group.name,
                        category: firstItem.item.category.name,
                      });
                    }}
                    sx={{
                      color: "#10527e",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    {isSelected ? (
                      <CheckBoxIcon />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              component="div"
              sx={{ flexGrow: 1 }}
            >
              {truncateString(group.name, 20)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Hersteller: {firstItem.item.producer.name}
          </Typography>
          {picUrl && (
            <CardMedia
              component="img"
              sx={{
                width: "auto",
                maxHeight: 100,
                maxWidth: 100,
                mt: "auto",
                mb: "auto",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
              image={strapiRemoteUri + picUrl}
              alt={group.name || ""}
            />
          )}
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">
              Anzahl: {group.items.length}
            </Typography>
            <hr style={{ borderTop: "1px solid #eee", margin: "8px 0" }} />
            <GroupItemProgressBar
              hasNeverCheckedItems={hasNeverCheckedItems}
              hasExpiredItems={hasExpiredItems}
              hasFailedCheckItems={hasFailedCheckItems}
            />
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ mt: "auto", justifyContent: "center" }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onOpen(containerId, group.documentId)}
          sx={{
            color: "#10527e",
            borderColor: "#10527e",
            marginBottom: "10px",
          }}
        >
          Öffnen
        </Button>
      </CardActions>
    </Card>
  );
};

/**
 * Recursively fetches container data and selects/deselects all its items and sub-containers.
 * @param {string} currentContainerId - The ID of the container to process.
 * @param {Function} setSelectedItems - The state setter function for selected items.
 * @param {string} token - The authentication token.
 * @param {string} action - 'select' or 'deselect'
 */
const recursivelySelectContainerItems = async (
  currentContainerId,
  setSelectedItems,
  token,
  action
) => {
  try {
    const containerDataRes = await fetchContainerWithCheck(
      currentContainerId,
      token
    );
    const containerData = containerDataRes.containers;

    setSelectedItems((prevSelectedItems) => {
      let newSelectedItems = [...prevSelectedItems];

      const containerItem = {
        type: "container",
        id: containerData.documentId, // Use containerData.documentId
        name: containerData.name,
      };

      if (action === "select") {
        // Add the current container itself to selected items if not already there
        if (
          !newSelectedItems.some(
            (selected) =>
              selected.type === "container" &&
              selected.id === containerData.documentId
          )
        ) {
          newSelectedItems.push(containerItem);
        }

        // Select all container_items
        containerData.container_items.forEach((item) => {
          // Corrected path
          if (
            !newSelectedItems.some(
              (selected) =>
                selected.type === "containerItem" &&
                selected.id === item.documentId
            )
          ) {
            newSelectedItems.push({
              type: "containerItem",
              id: item.documentId,
              name: item.item?.name,
              category: item.item?.category?.name,
            });
          }
        });
      } else if (action === "deselect") {
        // Deselect the current container itself
        newSelectedItems = newSelectedItems.filter(
          (selected) =>
            !(
              selected.type === "container" &&
              selected.id === containerData.documentId
            )
        );

        // Deselect all container_items
        containerData.container_items.forEach((item) => {
          // Corrected path
          newSelectedItems = newSelectedItems.filter(
            (selected) =>
              !(
                selected.type === "containerItem" &&
                selected.id === item.documentId
              )
          );
        });
      }

      return newSelectedItems;
    });

    // Recursively process sub-containers
    for (const subContainer of containerData.containers) {
      // Corrected path
      await recursivelySelectContainerItems(
        subContainer.documentId,
        setSelectedItems,
        token,
        action
      );
    }
  } catch (error) {
    console.error("Error recursively processing container items:", error);
    // Optionally, handle error more gracefully, e.g., show a toast notification
  }
};

export default function MergedGearCheckPage({ groupChangeKey }) {
  const { containerId, itemDocumentId } = useParams();
  const navigate = useNavigate();
  const [container, setContainer] = useState(null);
  const [role, setRole] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [isAddContainerModalOpen, setIsAddContainerModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isCheckModalOpen, setIsCheckModalOpen] = useState(false);
  const [showLatestCheckModal, setShowLatestCheckModal] = useState(false);
  const [latestCheckData, setLatestCheckData] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Effect to clear state when groupChangeKey changes
  useEffect(() => {
    setContainer(null);
    setRole(null);
    setBreadcrumbs([]);
    setDisplayedItems([]);
    setIsLoading(false);
    setError(null);
    setIsAddItemModalOpen(false);
    setIsAddContainerModalOpen(false);
    setSelectedItems([]);
    setIsCheckModalOpen(false);
    setShowLatestCheckModal(false);
    setLatestCheckData(null);
    setDeleteConfirmationOpen(false);
  }, [groupChangeKey]);

  useEffect(() => {
    const loadContainerData = async () => {
      if (!containerId) return;

      const token = localStorage.getItem("jwt");
      if (!token) {
        setError("Nicht eingeloggt. Bitte melden Sie sich an.");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await fetchContainerWithCheck(containerId);
        setContainer(data);
        processContainerData(data);
      } catch (error) {
        console.error("Fehler beim Laden des Containers:", error);
        setError(
          "Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut."
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadContainerData();
  }, [containerId, itemDocumentId]);

  /**
   * Handle opening the add item modal
   */
  const handleOpenAddItemModal = () => {
    setIsAddItemModalOpen(true);
  };

  /**
   * Handle closing the add item modal
   */
  const handleCloseAddItemModal = () => {
    setIsAddItemModalOpen(false);
  };

  /**
   * Handle opening the add container modal
   */
  const handleOpenAddContainerModal = () => {
    setIsAddContainerModalOpen(true);
  };

  /**
   * Handle closing the add container modal
   */
  const handleCloseAddContainerModal = () => {
    setIsAddContainerModalOpen(false);
  };

  /**
   * Handle successful container creation
   * Reloads the current container data
   */
  const handleContainerCreated = async () => {
    if (containerId) {
      setIsLoading(true);
      try {
        const data = await fetchContainerWithCheck(containerId);
        setContainer(data);
        processContainerData(data);
      } catch (error) {
        console.error("Fehler beim Neuladen des Containers:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Process container data and set state for breadcrumbs and displayed items
   * @param {Object} data - The container data from the API
   */
  const processContainerData = (data) => {
    if (!data?.containers) return;

    const containerData = data.containers;
    setRole(data?.role);
    let currentBreadcrumbs = [];
    let itemsToDisplay = [];

    // Add current container to breadcrumbs
    currentBreadcrumbs.push({
      name: containerData.name,
      path: `/gear/${containerData.documentId}`,
    });

    // Add parent container to breadcrumbs if it exists
    if (containerData.parent_container) {
      currentBreadcrumbs.unshift({
        name: containerData.parent_container.name,
        path: `/gear/${containerData.parent_container.documentId}`,
      });
    }

    if (itemDocumentId) {
      // Filter items by itemDocumentId
      const filteredItems = containerData.container_items.filter(
        (item) => item.item.documentId === itemDocumentId
      );

      if (filteredItems.length > 0) {
        itemsToDisplay = filteredItems;
        const itemName = filteredItems[0]?.item?.name || "Unbekanntes Item";
        currentBreadcrumbs.push({
          name: itemName,
          path: `/gear/${containerId}/${itemDocumentId}`,
        });
      }
    } else {
      // Group items by documentId
      const groupedItems = containerData.container_items.reduce((acc, item) => {
        const docId = item.item.documentId;
        if (!acc[docId]) {
          acc[docId] = [];
        }
        acc[docId].push(item);
        return acc;
      }, {});

      // Process grouped items
      for (const docId in groupedItems) {
        if (groupedItems[docId].length > 1) {
          // Multiple items with same documentId
          itemsToDisplay.push({
            type: "group",
            documentId: docId,
            name: groupedItems[docId][0].item.name,
            items: groupedItems[docId],
          });
        } else {
          // Single item
          itemsToDisplay.push(groupedItems[docId][0]);
        }
      }

      // Add sub-containers
      if (containerData.containers && containerData.containers.length > 0) {
        itemsToDisplay = [...containerData.containers, ...itemsToDisplay];
      }
    }

    setDisplayedItems(itemsToDisplay);
    setBreadcrumbs(currentBreadcrumbs);
  };

  /**
   * Handle opening a container
   * @param {string} id - The container ID to open
   */
  const handleOpenContainer = (id) => {
    navigate(`/gear/${id}`);
  };

  /**
   * Handle opening a group of items
   * @param {string} containerId - The container ID
   * @param {string} itemDocId - The item document ID
   */
  const handleOpenItemGroup = (containerId, itemDocId) => {
    navigate(`/gear/${containerId}/${itemDocId}`);
  };

  /**
   * Handle opening the check modal
   */
  const handleOpenCheckModal = () => {
    setIsCheckModalOpen(true);
  };

  /**
   * Handle closing the check modal
   */
  const handleCloseCheckModal = () => {
    setIsCheckModalOpen(false);
  };

  /**
   * Handle closing the latest check modal
   */
  const handleCloseLatestCheckModal = () => {
    setShowLatestCheckModal(false);
    setLatestCheckData(null);
  };

  /**
   * Handle successful check creation
   * Reloads the current container data and clears selections
   */
  const handleCheckCreated = async () => {
    if (containerId) {
      setIsLoading(true);
      try {
        const data = await fetchContainerWithCheck(containerId);
        setContainer(data);
        processContainerData(data);
        // Clear selections after successful check
        setSelectedItems([]);
      } catch (error) {
        console.error("Fehler beim Neuladen des Containers:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Handle selecting/deselecting an item
   * @param {Object} item - The item to select/deselect
   */
  const handleSelectItem = async (item) => {
    const token = localStorage.getItem("jwt");
    if (!token) {
      setError("Nicht eingeloggt. Bitte melden Sie sich an.");
      return;
    }

    if (item.type === "container") {
      const isCurrentlySelected = selectedItems.some(
        (selected) => selected.type === "container" && selected.id === item.id
      );
      const action = isCurrentlySelected ? "deselect" : "select";
      await recursivelySelectContainerItems(
        item.id,
        setSelectedItems,
        token,
        action
      );
    } else {
      // Existing logic for grouped items and single items
      setSelectedItems((prevSelectedItems) => {
        if (item.ids) {
          // This is a grouped item selection/deselection
          const groupDocumentIds = item.ids;
          const allGroupItemsSelected = groupDocumentIds.every((docId) =>
            prevSelectedItems.some(
              (selected) =>
                selected.type === "containerItem" && selected.id === docId
            )
          );

          let newSelectedItems = [...prevSelectedItems];

          if (allGroupItemsSelected) {
            // Deselect all items in the group
            newSelectedItems = newSelectedItems.filter(
              (selected) =>
                !(
                  selected.type === "containerItem" &&
                  groupDocumentIds.includes(selected.id)
                )
            );
          } else {
            // Select all items in the group that are not already selected
            groupDocumentIds.forEach((docId) => {
              if (
                !newSelectedItems.some(
                  (selected) =>
                    selected.type === "containerItem" && selected.id === docId
                )
              ) {
                newSelectedItems.push({
                  type: "containerItem",
                  id: docId,
                  name: item.name, // Use the group name for individual items
                  category: item.category, // Use the group category for individual items
                });
              }
            });
          }
          return newSelectedItems;
        } else {
          // This is a single item selection/deselection
          const existingItemIndex = prevSelectedItems.findIndex(
            (i) => i.type === item.type && i.id === item.id
          );

          if (existingItemIndex !== -1) {
            // Item is already selected, remove it
            return prevSelectedItems.filter(
              (_, index) => index !== existingItemIndex
            );
          } else {
            // Item is not selected, add it
            return [...prevSelectedItems, item];
          }
        }
      });
    }
  };

  /**
   * Check if an item is selected
   * @param {Object} item - The item to check
   * @returns {boolean} - Whether the item is selected
   */
  const isItemSelected = (item) => {
    if (item.type === "container") {
      return selectedItems.some(
        (i) => i.type === "container" && i.id === item.documentId
      );
    } else if (item.type === "group") {
      // Check if ALL items in the group are selected individually
      return item.items.every((groupItem) =>
        selectedItems.some(
          (selected) =>
            selected.type === "containerItem" &&
            selected.id === groupItem.documentId
        )
      );
    } else if (item.item) {
      return selectedItems.some(
        (i) => i.type === "containerItem" && i.id === item.documentId
      );
    }
    return false;
  };

  /**
   * Handle opening the delete confirmation dialog for selected items
   */
  const handleOpenDeleteConfirmation = () => {
    setDeleteConfirmationOpen(true);
  };

  /**
   * Handle closing the delete confirmation dialog
   */
  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
  };

  /**
   * Handle deleting selected items after confirmation
   */
  const deleteSelectedItems = async () => {
    setIsLoading(true);
    try {
      for (const item of selectedItems) {
        if (item.type === "containerItem") {
          if (item.ids) {
            // Grouped item
            for (const id of item.ids) {
              await deleteItem({
                type: "containerItem",
                id: id,
                containerId: containerId,
              });
            }
          } else {
            // Single item
            await deleteItem({
              type: "containerItem",
              id: item.id,
              containerId: containerId,
            });
          }
        } else if (item.type === "container") {
          await deleteItem({
            type: "container",
            id: item.id,
          });
        }
      }
      // Reload container data after successful deletion
      const data = await fetchContainerWithCheck(containerId);
      setContainer(data);
      processContainerData(data);
      setSelectedItems([]); // Clear selections after deletion
    } catch (error) {
      console.error("Fehler beim Löschen der ausgewählten Elemente:", error);
      setError(
        "Fehler beim Löschen der ausgewählten Elemente. Bitte versuchen Sie es später erneut."
      );
    } finally {
      setIsLoading(false);
      handleCloseDeleteConfirmation();
    }
  };

  if (isLoading) {
    return <Box sx={{ p: 2 }}>Laden...</Box>;
  }

  if (error) {
    return <Box sx={{ p: 2 }}>{error}</Box>;
  }

  return (
    <>
      <div className="index-page-container">
        <h2>Ausrüstung verwalten</h2>
        <p>
          Hier kannst du neue Ausrüstungsgegenstände hinzufügen, vorhandene
          bearbeiten und regelmäßige Prüfungen dokumentieren.
        </p>
      </div>
      <Box sx={{ flexGrow: 1, p: 1 }}>
        {/* Top Bar with Breadcrumbs and Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          {/* Breadcrumb Navigation */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ userSelect: "none" }}
          >
            {breadcrumbs.length > 1 &&
              breadcrumbs.map((crumb, index) => (
                <Link
                  key={crumb.path}
                  underline="hover"
                  color="inherit"
                  onClick={() => navigate(crumb.path)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  {crumb.name}
                </Link>
              ))}
          </Breadcrumbs>

          {/* Action Buttons */}
          <Box sx={{ minWidth: "90px" }}>
            {role === "admin" && (
              <Tooltip title="Aktionen">
                <IconButton
                  aria-label="actions"
                  aria-controls={openMenu ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openMenu ? "true" : undefined}
                  onClick={handleMenuClick}
                  sx={{
                    border: "2px solid #10527e",
                    margin: "2px",
                    backgroundColor: "#10527e",
                    color: "white",
                    "&:hover": {
                      border: "2px solid #282c34",
                      backgroundColor: "#282c34",
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {(role === "admin" || role === "checkOnly") && (
              <Tooltip title="Ausgewählte Items prüfen">
                <IconButton
                  color="secondary"
                  disabled={
                    selectedItems.filter(
                      (item) => item.type === "containerItem" && !item.ids
                    ).length === 0
                  }
                  onClick={handleOpenCheckModal}
                  sx={{
                    border: "2px solid #10527e",
                    margin: "2px",
                    color: "white",
                    backgroundColor: "#4ecc42ff",
                    "&:hover": {
                      border: "2px solid #282c34",
                      backgroundColor: "#282c34",
                    },
                  }}
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
            )}
            {role === "admin" && (
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                sx={{ mr: "20px", mt: "10px" }}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleOpenAddContainerModal();
                    handleMenuClose();
                  }}
                  disabled={itemDocumentId}
                >
                  <CreateNewFolderIcon sx={{ mr: 1 }} /> Neuen Container
                  erstellen
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleOpenAddItemModal();
                    handleMenuClose();
                  }}
                  disabled={itemDocumentId}
                >
                  <AddIcon sx={{ mr: 1 }} /> Neues Item hinzufügen
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleOpenDeleteConfirmation();
                    handleMenuClose();
                  }}
                  disabled={selectedItems.length === 0}
                >
                  <DeleteIcon sx={{ mr: 1 }} /> Ausgewählte Items löschen
                </MenuItem>
              </Menu>
            )}
          </Box>
        </Box>

        {/* Items Grid */}
        <Grid
          container
          justifyContent={"center"}
          spacing={3}
          marginBottom={"10px"}
        >
          {displayedItems.map((item, index) => (
            <Grid key={index} item>
              {/* Container Card */}
              {item.documentId && !item.type && item.name && (
                <ContainerCard
                  container={item}
                  onOpen={handleOpenContainer}
                  onSelect={() =>
                    handleSelectItem({
                      type: "container",
                      id: item.documentId,
                      name: item.name,
                    })
                  }
                  isSelected={isItemSelected({
                    type: "container",
                    documentId: item.documentId,
                    name: item.name,
                  })}
                  canSelect={role == "admin" || role == "checkOnly"}
                />
              )}

              {/* Grouped Items Card */}
              {item.type === "group" && (
                <GroupedItemCard
                  group={item}
                  containerId={containerId}
                  onOpen={handleOpenItemGroup}
                  onSelect={() =>
                    handleSelectItem({
                      type: "containerItem",
                      ids: item.items.map((groupItem) => groupItem.documentId),
                      name: item.name,
                      category: item.items[0].item.category.name,
                    })
                  }
                  isSelected={isItemSelected(item)}
                  canSelect={role == "admin" || role == "checkOnly"}
                />
              )}

              {/* Single Item Card */}
              {item.item && !item.type && (
                <SingleItemCard
                  item={item}
                  onSelect={() =>
                    handleSelectItem({
                      type: "containerItem",
                      id: item.documentId,
                      name: item.item?.name,
                      category: item.item?.category?.name,
                    })
                  }
                  isSelected={isItemSelected(item)}
                  setShowLatestCheckModal={setShowLatestCheckModal}
                  setLatestCheckData={setLatestCheckData}
                  canSelect={role == "admin" || role == "checkOnly"}
                />
              )}
            </Grid>
          ))}
        </Grid>

        {/* Modals */}
        <AddContainerModal
          open={isAddContainerModalOpen}
          onClose={handleCloseAddContainerModal}
          parentContainerId={containerId}
          onSuccess={handleContainerCreated}
        />

        <AddItemModal
          open={isAddItemModalOpen}
          onClose={handleCloseAddItemModal}
          containerId={containerId}
          onSuccess={handleContainerCreated}
        />

        <CheckModal
          open={isCheckModalOpen}
          onClose={handleCloseCheckModal}
          selectedItems={selectedItems}
          containerId={containerId}
          onSuccess={handleCheckCreated}
        />

        <LatestCheckModal
          open={showLatestCheckModal}
          onClose={handleCloseLatestCheckModal}
          checkData={latestCheckData}
        />

        {/* Delete Confirmation Dialog for selected items */}
        <Dialog
          open={deleteConfirmationOpen}
          onClose={handleCloseDeleteConfirmation}
          aria-labelledby="delete-confirmation-title"
          aria-describedby="delete-confirmation-description"
        >
          <DialogTitle id="delete-confirmation-title">
            {"Ausgewählte Elemente löschen?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-confirmation-description">
              Sind Sie sicher, dass Sie die {selectedItems.length} ausgewählten
              Elemente löschen möchten?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteConfirmation}>Abbrechen</Button>
            <Button onClick={deleteSelectedItems} autoFocus>
              Löschen
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
